/**
 * Parsers for earn position + acknowledgment routes:
 *   - POST `/earn/vaults/positions`
 *   - POST `/earn/portfolio/positions`
 *   - POST `/earn/vaults/acknowledge` (response has no parsing — boolean only)
 *
 * Exports `parseEarnWithdrawalRequestFromApi` for reuse by the queue parser.
 */

import { parseU64Optional, parseU64OrZero } from "@bridgesplit/utils";

import { EarnUserPositionApi, EarnVaultPositionResponseApi, EarnWithdrawalRequestApi } from "../api/earn-positions";
import {
    EarnUserPosition,
    EarnVaultPositionResponse,
    EarnWithdrawalRequest,
    EarnWithdrawalRequestStatus
} from "../domain/earn-positions";
import { parseEarnVaultFromApi } from "./earn-parser";

function parseEarnUserPositionFromApi(api: EarnUserPositionApi): EarnUserPosition {
    return {
        earnVaultAddress: api.earnVaultAddress,
        userWallet: api.userWallet,
        shareBalance: parseU64OrZero(api.lpBalance ?? api.shareBalance),
        lpBalance: parseU64Optional(api.lpBalance),
        costBasisAssets: parseU64OrZero(api.costBasisAssets),
        realizedPnlAssets: parseU64OrZero(api.realizedPnlAssets),
        feesPaidAssets: parseU64OrZero(api.feesPaidAssets),
        acknowledgedAt: api.acknowledgedAt ?? undefined,
        lastSyncedSlot: parseU64OrZero(api.lastSyncedSlot)
    };
}

/** Exported for reuse by {@link ./earn-queue-parser}. */
export function parseEarnWithdrawalRequestFromApi(api: EarnWithdrawalRequestApi): EarnWithdrawalRequest {
    return {
        onChainRequestAddr: api.onChainRequestAddr,
        earnVaultAddress: api.earnVaultAddress,
        userWallet: api.userWallet,
        sharesRequested: parseU64OrZero(api.sharesRequested),
        sharesFilled: parseU64OrZero(api.sharesFilled),
        sharesClaimed: parseU64OrZero(api.sharesClaimed),
        requestedAt: api.requestedAt,
        fulfilledAt: api.fulfilledAt ?? undefined,
        cancelledAt: api.cancelledAt ?? undefined,
        status: api.status as EarnWithdrawalRequestStatus,
        payoutMint: api.payoutMint ?? undefined,
        fills: (api.fills ?? []).map((fill) => ({ mint: fill.mint, amount: parseU64OrZero(fill.amount) }))
    };
}

export function parseEarnVaultPositionResponseFromApi(api: EarnVaultPositionResponseApi): EarnVaultPositionResponse {
    return {
        vault: parseEarnVaultFromApi(api.vault),
        position: api.position ? parseEarnUserPositionFromApi(api.position) : undefined,
        openWithdrawalRequests: api.openWithdrawalRequests.map(parseEarnWithdrawalRequestFromApi)
    };
}

export function parseEarnPortfolioPositionsFromApi(api: EarnVaultPositionResponseApi[]): EarnVaultPositionResponse[] {
    return api.map(parseEarnVaultPositionResponseFromApi);
}
